import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { Button } from "@design-system/Button/Button";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    SCHEDULE_APPLICATIONS_EMPTY_VALUE,
    ScheduleApplicationJournalEntryRef,
    ScheduleApplicationRow,
} from "../scheduleApplicationRowHelpers";
import { useScheduleApplicationsTableMeta } from "../scheduleApplicationsTableMeta";

interface JournalEntryLinkProps {
    reference: ScheduleApplicationJournalEntryRef | null;
    entityId: number;
    ledgerId: string;
    ledgerBasis: AccountingLedgerBasis;
    onOpenJournalEntry:
        | ((options: {
              referenceNumber: string;
              entityId: number;
              ledgerId: string;
              ledgerBasis: AccountingLedgerBasis;
          }) => void)
        | undefined;
}

const JournalEntryLink = ({
    reference,
    entityId,
    ledgerId,
    ledgerBasis,
    onOpenJournalEntry,
}: JournalEntryLinkProps) => {
    if (!reference) {
        return <>{SCHEDULE_APPLICATIONS_EMPTY_VALUE}</>;
    }
    return (
        <Button
            variant="link"
            size="xs"
            onClick={(event) => {
                event.stopPropagation();
                onOpenJournalEntry?.({
                    referenceNumber: reference.referenceNumber,
                    entityId,
                    ledgerId,
                    ledgerBasis,
                });
            }}
        >
            {reference.label}
        </Button>
    );
};

export const ScheduleApplicationReferenceCell = memo(
    ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        ScheduleApplicationRow,
        string | null
    >) => {
        const meta = useScheduleApplicationsTableMeta(table);
        const rowData = row.original;
        return (
            <ViewTableCell>
                <JournalEntryLink
                    reference={rowData.reference}
                    entityId={rowData.entityId}
                    ledgerId={rowData.ledgerId}
                    ledgerBasis={rowData.ledgerBasis}
                    onOpenJournalEntry={meta.callbacks?.onOpenJournalEntry}
                />
            </ViewTableCell>
        );
    },
);
ScheduleApplicationReferenceCell.displayName =
    "ScheduleApplicationReferenceCell";

export const ScheduleApplicationDescriptionCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, ScheduleApplicationRow, string>) => {
        const rowData = row.original;
        return (
            <ViewTableCell>
                {rowData.description || SCHEDULE_APPLICATIONS_EMPTY_VALUE}
            </ViewTableCell>
        );
    },
);
ScheduleApplicationDescriptionCell.displayName =
    "ScheduleApplicationDescriptionCell";

export const ScheduleApplicationLineItemCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        ScheduleApplicationRow,
        string | null
    >) => {
        const rowData = row.original;
        if (rowData.rowType !== "application") {
            return (
                <ViewTableCell>
                    {SCHEDULE_APPLICATIONS_EMPTY_VALUE}
                </ViewTableCell>
            );
        }
        return (
            <ViewTableCell>
                {rowData.lineItemLabel ?? SCHEDULE_APPLICATIONS_EMPTY_VALUE}
            </ViewTableCell>
        );
    },
);
ScheduleApplicationLineItemCell.displayName = "ScheduleApplicationLineItemCell";
